import { TagCode } from "./tag-codes"
import { TagSize, TagType } from "./tag-types"
import { DataStream } from "./data-stream"

export interface IFDEntry {
	tag: TagCode
	type: TagType
	value: number | ArrayLike<number>
}

function isArrayLike(val: unknown): val is ArrayLike<number> {
	if (val && typeof val === "object" && "length" in val) {
		return typeof val?.length === "number"
	}

	return false
}

function needsPaddingByte(entry: IFDEntry) {
	return entry.type === TagType.ASCII
}

function getTagCount(entry: IFDEntry) {
	if (isArrayLike(entry.value)) {
		return needsPaddingByte(entry) ? entry.value.length + 1 : entry.value.length
	} else {
		return 1
	}
}

function getTagSize(entry: IFDEntry) {
	const valueSize = TagSize[entry.type]
	const valueCount = getTagCount(entry)

	return valueSize * valueCount
}

export function getDirectorySize(entries: IFDEntry[]) {
	let totalSize = entries.length * 12 + 6

	for (const element of entries) {
		const tagSize = getTagSize(element)

		if (tagSize > 4) {
			totalSize += (tagSize + 1) & ~1
		}
	}

	return totalSize
}

function writeTagValue(stream: DataStream, entry: IFDEntry) {
	if (isArrayLike(entry.value)) {
		for (let i = 0; i < getTagCount(entry); i++) {
			stream[entry.type].set(entry.value[i] || 0)
		}
	} else {
		stream[entry.type].set(entry.value)
	}
}

export function writeTiffHeader(stream: DataStream) {
	stream.setUint16(stream.littleEndian ? 0x4949 : 0x4d4d) // byte order
	stream.setUint16(42) // 42
	stream.setUint32(8) // 1st IFD offset
}

export function writeDirectory(
	stream: DataStream,
	entries: IFDEntry[],
	baseOffset = stream.offset,
	nextIFD = 0,
) {
	let valuesOffset = baseOffset + entries.length * 12 + 6 // (6 = entries count + next IFD offset)

	stream.setUint16(entries.length) // Entries count

	for (const entry of entries) {
		let tagSize = getTagSize(entry)

		stream.setUint16(entry.tag)
		stream.setUint16(entry.type)
		stream.setUint32(getTagCount(entry))

		if (tagSize <= 4) {
			// Only write values which fit within the IFD boundary
			writeTagValue(stream, entry)

			while (tagSize < 4) {
				// Padding
				stream.setUint8(0)
				tagSize++
			}
		} else {
			// Write offset to stored values
			stream.setUint32(valuesOffset)

			valuesOffset += (tagSize + 1) & ~1
		}
	}

	stream.setUint32(nextIFD) // Next IFD offset

	for (const entry of entries) {
		const tagSize = getTagSize(entry)

		if (tagSize > 4) {
			writeTagValue(stream, entry)

			if (tagSize & 1) {
				// Padding
				stream.setUint8(0)
			}
		}
	}
}
